import React from "react";
import cartService, { Cart } from "../../../../Service/cart.service";
import { Plan } from "../../../../Entities/Plan";
import { Button } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { Typography } from "antd";
import { extractBasePrice, formatPlanName } from "../../../../Utils/planUtils";
import { useAppContext } from "../../../../Context/AppContext/AppContext";

interface AddonCardProps {
  addon: Plan;
  userCart: Cart;
  activePlan: Plan | null;
  userCurrencyCode: "INR" | "USD";
  userCurrencySymbol: "₹" | "$";
  mode: "upgrade" | "addon_only" | "renew";
}

const AddonCard: React.FC<AddonCardProps> = ({
  addon,
  userCart,
  activePlan,
  userCurrencyCode,
  userCurrencySymbol,
  mode,
}) => {
  const { plans } = useAppContext();

  const selectedBasePlan = plans.find(
    (plan) => plan._id === userCart.basePlan.planId,
  );

  const addonPrice = extractBasePrice(
    addon.variants.find(
      (v) => v.currency.toLowerCase() === userCurrencyCode.toLowerCase(),
    )?.price || 0,
    userCurrencyCode,
  );

  const formmatedAddon = {
    _id: addon._id,
    name: addon.name.toLowerCase().includes("assessment")
      ? "Assessment Add-on"
      : "Job Posts Add-on",
    price: addonPrice,
    quantity: addon.limits.applicationsProcessed || addon.limits.jobPosts,
    type: addon.name.toLowerCase().includes("assessment")
      ? "application"
      : "jobPost",
    description: addon.name.toLowerCase().includes("assessment")
      ? `Get additional ${addon.limits.applicationsProcessed} Assessments`
      : `Get additional ${addon.limits.jobPosts} active job posts`,
  };

  const currentQuantity =
    userCart.addons.find((item) => item.addonId === addon._id)?.quantity || 0;

  const getTotalNoOfApplications = () => {
    const addonQuantity = currentQuantity * formmatedAddon.quantity;
    const planQuantity =
      mode === "upgrade"
        ? selectedBasePlan?.limits?.applicationsProcessed || 0
        : activePlan?.limits?.applicationsProcessed || 0;
    return addonQuantity + planQuantity;
  };

  const getTotalNoOfJobPosts = () => {
    const addonQuantity = currentQuantity * formmatedAddon.quantity;
    const planQuantity =
      mode === "upgrade"
        ? selectedBasePlan?.limits?.jobPosts || 0
        : activePlan?.limits?.jobPosts || 0;
    return addonQuantity + planQuantity;
  };

  const getCurrentPlanName = () => {
    const basePlan = mode === "upgrade" ? selectedBasePlan : activePlan;
    return formatPlanName(basePlan?.name ? basePlan?.name : "NA");
  };

  const getCurrentPLanNoOfApplications = () => {
    const basePlan = mode === "upgrade" ? selectedBasePlan : activePlan;
    return basePlan?.limits?.applicationsProcessed || 0;
  };

  const getCurrentPLanNoOfJobPosts = () => {
    const basePlan = mode === "upgrade" ? selectedBasePlan : activePlan;
    return basePlan?.limits?.jobPosts || 0;
  };

  const handleClear = (planId: string) => {
    cartService.clearAddons(planId);
  };

  const handleIncrement = (planId: string) => {
    cartService.setSelectedAddons(planId);
  };

  return (
    <section className="addon-card">
      {/* Plan name and description */}
      <div style={{ textAlign: "center" }}>
        <h2
          style={{
            fontSize: "20px",
            fontWeight: "600",
            margin: "0 0 8px 0",
          }}
        >
          {formmatedAddon.name}
        </h2>
        <p
          style={{
            fontSize: "14px",
            color: "#666",
            margin: "0 0 16px 0",
          }}
        >
          {formmatedAddon.description}
        </p>

        {/* Price */}
        <div
          style={{
            borderBottom: "1px solid #eee",
            paddingBottom: "16px",
            textAlign: "center",
          }}
        >
          <span
            style={{
              fontSize: "32px",
              fontWeight: "700",
              color: "#000",
            }}
          >
            {userCurrencySymbol}
            {formmatedAddon.price}
          </span>
          {userCurrencyCode === "INR" && (
            <div style={{ fontSize: "12px", color: "#888", marginTop: "4px" }}>
              + taxes
            </div>
          )}
        </div>
      </div>

      <div className="addon-card-button-group">
        <Button
          shape="round"
          size="middle"
          type="primary"
          icon={<PlusOutlined />}
          block
          onClick={() => handleIncrement(addon._id)}
        >
          Add
        </Button>
        <Button
          shape="round"
          size="middle"
          block
          onClick={() => handleClear(addon._id)}
        >
          Clear
        </Button>
      </div>

      {/* Summary */}
      <div className="addon-card-summary">
        <Typography.Text style={{ margin: 0 }}>
          <span>{getCurrentPlanName()}</span>
          <span>
            {formmatedAddon.type === "application"
              ? getCurrentPLanNoOfApplications() === -1
                ? "Unlimited"
                : getCurrentPLanNoOfApplications()
              : activePlan?.limits?.jobPosts === -1
                ? "Unlimited"
                : getCurrentPLanNoOfJobPosts()}
          </span>
        </Typography.Text>
        <Typography.Text style={{ margin: 0 }}>
          <span>Addon ({currentQuantity})</span>
          <span>{currentQuantity * formmatedAddon.quantity}</span>
        </Typography.Text>
        <Typography.Text style={{ margin: 0 }}>
          <span>
            {formmatedAddon.type === "application"
              ? "Total Assessments"
              : "Total Job Posts"}
          </span>
          <span>
            {formmatedAddon.type === "application"
              ? getTotalNoOfApplications()
              : getTotalNoOfJobPosts()}
          </span>
        </Typography.Text>
      </div>
    </section>
  );
};

export default AddonCard;
